/**
 * Unified identity labels for GlobalHeader across ERP / Finance / HRMS / CRM / Portal.
 *
 * Firestore `users/{uid}` documents use mixed fields: `displayName`, `name`, `profile.name`.
 * Always prefer the same resolution order everywhere.
 *
 * Note: Each *.web.app host is a different browser origin — Firebase Auth sessions are
 * separate per subdomain unless you use a single domain + paths or a formal SSO handoff.
 */

export function resolveUserDisplayName(
  userDoc: Record<string, unknown> | null | undefined,
  authEmail?: string | null
): string {
  if (!userDoc || typeof userDoc !== 'object') {
    return authEmail?.split('@')[0] || 'User';
  }
  const d = userDoc as Record<string, any>;
  const candidates = [d.displayName, d.profile?.name, d.name];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) {
      return c.trim();
    }
  }
  return authEmail?.split('@')[0] || 'User';
}

/** Role badge in the global header (consistent casing / synonyms). */
export function formatRoleLabel(role: string | null | undefined): string {
  if (!role) return 'User';
  const r = String(role).toLowerCase().replace(/_/g, ' ').trim();
  if (r === 'superadmin' || r === 'super admin' || r === 'super_admin') return 'Super Admin';
  if (r === 'finance manager' || r === 'finance_manager') return 'Finance Manager';
  if (r === 'hr manager' || r === 'hr_manager') return 'HR Manager';
  if (r === 'erp manager' || r === 'erp_manager') return 'ERP Manager';
  if (r === 'admissions counselor' || r === 'admissions_counselor') return 'Admissions Counselor';
  return r
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Teachers / educators should only get ERP + HRMS (+ LMS when live) in module switchers —
 * not CRM or Finance. Accepts Firestore role strings or display labels from {@link formatRoleLabel}
 * (e.g. "Teacher" vs "teacher").
 */
export function isTeacherModuleScope(role: string | null | undefined): boolean {
  if (!role) return false;
  const r = String(role).toLowerCase().replace(/_/g, ' ').trim();
  return r === 'teacher' || r === 'educator';
}
